import React, { Component, Fragment } from 'react'
import { Formik, Field } from 'formik'
import { useMedia } from 'react-use'

// utils
import checkRoles from 'utils/sef/checkRoles'
import logos from 'utils/logos'
import checkPermissions from 'utils/sef/checkRoutingPermissions'

// styles
import {
  BrowniePointsLogo,
  DashboardMobileHeading,
  DashboardHeading,
  DashboardHomePageMobileContainer,
  DashboardSideNavOuter,
  DashCollapseIcon,
  DashboardSettingsWrapper,
  DashSidebarConfigOption,
  DashSettingsIcon,
  FooterLink,
  MobileMenuLabel,
  MobileNavItemGroupWrapper,
  MobileNavItemGroupWrapperInactive,
  NavItems,
  SidebarFooterLogoWrapper,
  SidebarFooter
} from './styles'

// assets
// import HasLocationIcon from 'assets/images/icons/opp-location.svg?react'
import { SolidHeart } from 'components/Icons/Icons'
import OppIcon from 'assets/images/svgs/icon-org-page-opportunity.svg?react'
import DonationIcon from 'assets/images/cause-type-icons/money_icon_dark.svg?react'
import OrgIcon from 'assets/images/svgs/icon-org-page-organisation.svg?react'
import BpEventsIcon from 'assets/images/svgs/icon-bp-events.svg?react'
import EmailActivityIcon from 'assets/images/svgs/icon-email-activity.svg?react'

// config
import l, { c } from 'config/localization'

// icons
import { BugAnt } from '@styled-icons/heroicons-solid/BugAnt'
import { TableCells } from '@styled-icons/heroicons-solid/TableCells'
import { AddressBook } from '@styled-icons/fa-solid/AddressBook'
import { Sitemap } from '@styled-icons/fa-solid/Sitemap'
import { RankingStar } from '@styled-icons/fa-solid/RankingStar'
import { Crown } from '@styled-icons/fa-solid/Crown'
import { FilePdf } from '@styled-icons/fa-solid/FilePdf'
import { Receipt as FaReceipt } from '@styled-icons/fa-solid/Receipt'
import { AppFolder } from '@styled-icons/fluentui-system-filled/AppFolder'
import { BuildingHouse } from '@styled-icons/boxicons-regular/BuildingHouse'
import { BusinessCenter } from '@styled-icons/material-rounded/BusinessCenter'
import { Dashboard } from '@styled-icons/boxicons-solid'
import { DashboardSideNav, DashboardNavItem } from '../Dashboard'
import { DataArea } from '@styled-icons/fluentui-system-regular/DataArea'
import { Handshake } from '@styled-icons/fa-solid/Handshake'
import { ClipboardList } from '@styled-icons/fa-solid/ClipboardList'
import { Boxes, PeopleFill as People, Receipt } from '@styled-icons/bootstrap'
import { Results } from '@styled-icons/foundation/Results'
import { ArchiveDrawer, Team } from '@styled-icons/remix-fill'
import { Tags } from '@styled-icons/fa-solid/Tags'
import { AdminPanelSettings } from '@styled-icons/material/AdminPanelSettings'
import { School } from '@styled-icons/material/School'
import { WorkHistory } from '@styled-icons/material/WorkHistory'
import { Storefront } from '@styled-icons/material/Storefront'
import { Wrench } from 'components/Icons/Icons'
import { Workspaces } from '@styled-icons/material/Workspaces'
import { DocumentPdf } from '@styled-icons/fluentui-system-regular/DocumentPdf'
import { FileInvoiceDollar } from '@styled-icons/fa-solid/FileInvoiceDollar'
import { JournalText } from '@styled-icons/bootstrap/JournalText'
import { GiftFill } from '@styled-icons/bootstrap/GiftFill'
import { Calendar2Range } from '@styled-icons/bootstrap/Calendar2Range'

// Load components synchronously
import CheckboxInput from 'components/CheckboxInput'
import ConditionalDisplay from 'components/ConditionalDisplay/ConditionalDisplay'
import LinkButton from 'components/SmallButtonPrimary/LinkButton'
import SmallButtonPrimary from 'components/SmallButtonPrimary/SmallButtonPrimary'

const { dashSidebarLogo } = logos

const PureComponentSidebar = ({
  isParticipant,
  locationInfo,
  expandedMenuGroup,
  roles,
  sefRoles,
  sidebarIsCollapsed,
  toggleDashSidebarSettings,
  toggleMenuItem,
  toggleSidebarCollapse,
  workspaceDetails
}) => {
  const isMobile = useMedia('(max-width: 769px)')

  const canViewEvents = checkPermissions({
    featureName: 'events',
    sefRoles,
    workspaceDetails
  })
  const canViewReports = checkPermissions({
    featureName: 'reports',
    sefRoles,
    workspaceDetails
  })
  const canViewDocuments = checkPermissions({
    featureName: 'agreements',
    sefRoles,
    workspaceDetails
  })
  const canViewForms = checkPermissions({
    featureName: 'forms',
    sefRoles,
    workspaceDetails
  })
  const canViewAttendance = checkPermissions({
    featureName: 'attendances',
    roles,
    sefRoles,
    workspaceDetails
  })
  const canViewBudgetCategories = checkPermissions({
    featureName: 'budgets',
    sefRoles,
    workspaceDetails
  })
  const canViewPurchases = checkPermissions({
    featureName: 'purchases',
    sefRoles,
    workspaceDetails
  })
  const canViewSuppliers = checkPermissions({
    featureName: 'suppliers',
    sefRoles,
    workspaceDetails
  })
  const canViewOutputAreas = checkPermissions({
    featureName: 'areas',
    sefRoles,
    workspaceDetails
  })
  const canViewIndicators = checkPermissions({
    featureName: 'indicators',
    sefRoles,
    workspaceDetails
  })
  const canViewOutputs = checkPermissions({
    featureName: 'outputs',
    sefRoles,
    workspaceDetails
  })
  const canViewWorksites = checkPermissions({
    featureName: 'worksites',
    sefRoles,
    workspaceDetails
  })
  const canViewParticipants = checkPermissions({
    featureName: 'participants',
    sefRoles,
    workspaceDetails
  })
  const canViewBulkActions = checkPermissions({
    featureName: 'bulkActions',
    sefRoles,
    workspaceDetails
  })
  const canViewPayroll = checkPermissions({
    featureName: 'payroll',
    sefRoles,
    workspaceDetails
  })
  const canViewPayslips = checkPermissions({
    featureName: 'payslips',
    sefRoles,
    workspaceDetails
  })
  const canViewTrainingRecords = checkPermissions({
    featureName: 'trainingRecords',
    isParticipant,
    sefRoles,
    workspaceDetails
  })
  const canViewWorkRecords = checkPermissions({
    featureName: 'workRecords',
    isParticipant,
    sefRoles,
    workspaceDetails
  })
  const canViewSideHustles = checkPermissions({
    featureName: 'sideHustles',
    isParticipant,
    sefRoles,
    workspaceDetails
  })
  const canViewAdministrativeOpts = checkPermissions({
    featureName: 'admin',
    isParticipant,
    sefRoles,
    workspaceDetails
  })

  const canViewUserDebug = sefRoles.isGodAdmin
  const canViewImpactOpts = canViewOutputAreas || canViewIndicators || canViewOutputs

  const canViewPartners =
    sefRoles.isGodAdmin ||
    sefRoles.isWorkspaceAdmin ||
    sefRoles.isWorkspaceProjectAdmin ||
    sefRoles.isPartnerSigningAdmin

  const canViewNetwork =
    sefRoles.isGodAdmin ||
    sefRoles.isWorkspaceAdmin ||
    sefRoles.isWorkspaceProjectAdmin ||
    sefRoles.isPartnerSigningAdmin

  const canViewFinance =
    canViewPayslips ||
    canViewPayroll ||
    canViewPurchases ||
    canViewSuppliers ||
    canViewBudgetCategories

  const canViewGrowth = canViewTrainingRecords || canViewSideHustles || canViewWorkRecords

  const canViewAdmins =
    sefRoles.isGodAdmin ||
    sefRoles.isSuperAdmin ||
    sefRoles.isPartnerAdmin ||
    sefRoles.isWorkspaceAdmin ||
    sefRoles.isWorkspaceFinanceAdmin ||
    sefRoles.isWorkspaceProcurementAdmin ||
    sefRoles.isWorkspaceProjectAdmin ||
    sefRoles.isWorkspaceSigningAdmin

  const canViewPrograms = sefRoles.isGodAdmin || sefRoles.isPlatformAdmin
  const canViewWorkspaces = sefRoles.isGodAdmin || sefRoles.isPlatformAdmin
  const canViewProjects =
    sefRoles.isGodAdmin ||
    sefRoles.isPlatformAdmin ||
    sefRoles.isWorkspaceAdmin ||
    sefRoles.isWorkspaceProjectAdmin

  const canViewTeamOpts = canViewAdmins || sefRoles.isPartnerSigningAdmin

  const isOnAdminPage =
    locationInfo.agreementsActive ||
    locationInfo.reportsActive ||
    locationInfo.platformEventsPathActive ||
    locationInfo.formsActive
  const isOnAcePage =
    locationInfo.skillsTrainingActive ||
    locationInfo.workRecordsActive ||
    locationInfo.sideHustlesActive
  const isOnTeamPage =
    locationInfo.participantsActive || locationInfo.attendanceActive || locationInfo.adminsActive
  const isOnNetworkPage =
    locationInfo.workspacesActive ||
    locationInfo.partnersActive ||
    locationInfo.projectsActive ||
    locationInfo.programsActive ||
    locationInfo.worksitesActive
  const isOnFinancePage =
    locationInfo.payrollActive ||
    locationInfo.budgetsActive ||
    locationInfo.budgetCategoriesActive ||
    locationInfo.suppliersActive ||
    locationInfo.purchaseRequestsActive
  const isOnImpactPage =
    locationInfo.outputAreasActive || locationInfo.indicatorsActive || locationInfo.outputsActive

  const displayNetworkOpts = isMobile || expandedMenuGroup === 'Network' || isOnNetworkPage
  const displayImpactOpts = isMobile || expandedMenuGroup === 'Impact' || isOnImpactPage
  const displayTeamOpts = isMobile || expandedMenuGroup === 'Team' || isOnTeamPage
  const displayFinanceOpts = isMobile || expandedMenuGroup === 'Finance' || isOnFinancePage
  const displayAdminOpts = isMobile || expandedMenuGroup === 'Admin' || isOnAdminPage
  const displayAceOpts = isMobile || expandedMenuGroup === 'ACE' || isOnAcePage

  return (
    <Fragment>
      <DashboardHomePageMobileContainer displayMobile={locationInfo.homeActive}>
        <DashboardMobileHeading>Dashboard Menu</DashboardMobileHeading>
      </DashboardHomePageMobileContainer>

      <DashboardSideNavOuter
        displayMobile={locationInfo.homeActive}
        sidebarIsCollapsed={sidebarIsCollapsed}
      >
        <DashboardSideNav
          displayMobile={locationInfo.homeActive}
          sidebarIsCollapsed={sidebarIsCollapsed}
        >
          <NavItems id="dashboard-menu-items-wrapper">
            <DashboardNavItem
              hiddenOnMobile={locationInfo.homeActive}
              Icon={Dashboard}
              hover="exception"
              id={locationInfo.homeActive ? 'active-dash-nav-item' : undefined}
              isActive={locationInfo.homeActive}
              label="Home"
              onClick={() => toggleMenuItem('Home')}
              sidebarIsCollapsed={sidebarIsCollapsed}
              to="/dashboard"
            />

            <ConditionalDisplay displayWhen={[canViewNetwork]}>
              <MobileMenuLabel>Network</MobileMenuLabel>

              <DashboardNavItem
                hasActiveChild={expandedMenuGroup === 'Network' || isOnNetworkPage}
                // isActive={locationInfo.workspacesActive}
                hiddenOnMobile={isMobile}
                Icon={Sitemap}
                hover="exception"
                id={locationInfo.homeActive ? 'active-dash-nav-item' : undefined}
                label="Network"
                onClick={() => toggleMenuItem('Network')}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[displayNetworkOpts]}>
              <MobileNavItemGroupWrapper>
                <ConditionalDisplay displayWhen={[canViewWorkspaces]}>
                  <DashboardNavItem
                    hover="exception"
                    marginLeft="15px"
                    Icon={Workspaces}
                    isActive={locationInfo.workspacesActive}
                    id={locationInfo.workspacesActive ? 'active-dash-nav-item' : undefined}
                    label="Workspaces"
                    sidebarIsCollapsed={sidebarIsCollapsed}
                    to={'/dashboard/workspaces'}
                  />
                </ConditionalDisplay>

                <ConditionalDisplay displayWhen={[canViewPrograms]}>
                  <DashboardNavItem
                    hover="exception"
                    Icon={Boxes}
                    marginLeft="15px"
                    isActive={locationInfo.programsActive}
                    id={locationInfo.programsActive ? 'active-dash-nav-item' : undefined}
                    label="Programmes"
                    sidebarIsCollapsed={sidebarIsCollapsed}
                    to={'/dashboard/programs'}
                  />
                </ConditionalDisplay>

                <ConditionalDisplay displayWhen={[canViewProjects]}>
                  <DashboardNavItem
                    hover="exception"
                    Icon={ArchiveDrawer}
                    marginLeft="15px"
                    isActive={locationInfo.projectsActive}
                    id={locationInfo.projectsActive ? 'active-dash-nav-item' : undefined}
                    label="Projects"
                    sidebarIsCollapsed={sidebarIsCollapsed}
                    to={'/dashboard/workspace-projects'}
                  />
                </ConditionalDisplay>

                <ConditionalDisplay displayWhen={[canViewPartners]}>
                  <DashboardNavItem
                    label="Partners"
                    Icon={Handshake}
                    marginLeft="15px"
                    to={'/dashboard/partners'}
                    isActive={locationInfo.partnersActive}
                    hover="exception"
                    id={locationInfo.partnersActive ? 'active-dash-nav-item' : undefined}
                    sidebarIsCollapsed={sidebarIsCollapsed}
                  />
                </ConditionalDisplay>

                <ConditionalDisplay displayWhen={[canViewWorksites]}>
                  <DashboardNavItem
                    label="Locations"
                    Icon={BuildingHouse}
                    marginLeft="15px"
                    to={'/dashboard/worksites'}
                    isActive={locationInfo.worksitesActive}
                    id={locationInfo.worksitesActive ? 'active-dash-nav-item' : undefined}
                    sidebarIsCollapsed={sidebarIsCollapsed}
                  />
                </ConditionalDisplay>
              </MobileNavItemGroupWrapper>
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewImpactOpts]}>
              <MobileMenuLabel>Impact</MobileMenuLabel>

              <DashboardNavItem
                hasActiveChild={expandedMenuGroup === 'Impact' || isOnImpactPage}
                hiddenOnMobile={isMobile}
                Icon={RankingStar}
                hover="exception"
                id={locationInfo.homeActive ? 'active-dash-nav-item' : undefined}
                label="Impact"
                onClick={() => toggleMenuItem('Impact')}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />

              <ConditionalDisplay displayWhen={[displayImpactOpts]}>
                <MobileNavItemGroupWrapper>
                  <ConditionalDisplay displayWhen={[canViewOutputAreas]}>
                    <DashboardNavItem
                      label="Themes"
                      hover="exception"
                      Icon={AppFolder}
                      marginLeft="15px"
                      to={'/dashboard/output-areas'}
                      isActive={locationInfo.outputAreasActive}
                      id={locationInfo.outputAreasActive ? 'active-dash-nav-item' : undefined}
                      sidebarIsCollapsed={sidebarIsCollapsed}
                    />
                  </ConditionalDisplay>

                  <ConditionalDisplay displayWhen={[canViewIndicators]}>
                    <DashboardNavItem
                      label="Indicators"
                      hover="exception"
                      Icon={DataArea}
                      marginLeft="15px"
                      to={'/dashboard/output-indicators'}
                      isActive={locationInfo.indicatorsActive}
                      id={locationInfo.indicatorsActive ? 'active-dash-nav-item' : undefined}
                      sidebarIsCollapsed={sidebarIsCollapsed}
                    />
                  </ConditionalDisplay>

                  <ConditionalDisplay displayWhen={[canViewOutputs]}>
                    <DashboardNavItem
                      label="Outputs"
                      Icon={Results}
                      marginLeft="15px"
                      to={'/dashboard/outputs'}
                      isActive={locationInfo.outputsActive}
                      hover="exception"
                      id={locationInfo.outputsActive ? 'active-dash-nav-item' : undefined}
                      sidebarIsCollapsed={sidebarIsCollapsed}
                    />
                  </ConditionalDisplay>
                </MobileNavItemGroupWrapper>
              </ConditionalDisplay>
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewTeamOpts]}>
              <MobileMenuLabel>Team</MobileMenuLabel>

              <DashboardNavItem
                hasActiveChild={expandedMenuGroup === 'Team' || isOnTeamPage}
                hiddenOnMobile={isMobile}
                hover="exception"
                Icon={AddressBook}
                id={locationInfo.homeActive ? 'active-dash-nav-item' : undefined}
                label="Team"
                onClick={() => toggleMenuItem('Team')}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />

              <ConditionalDisplay displayWhen={[displayTeamOpts]}>
                <MobileNavItemGroupWrapper>
                  <ConditionalDisplay displayWhen={[canViewParticipants]}>
                    <DashboardNavItem
                      label="Participants"
                      marginLeft="15px"
                      Icon={People}
                      hover="exception"
                      to={'/dashboard/participants'}
                      isActive={locationInfo.participantsActive}
                      id={locationInfo.participantsActive ? 'active-dash-nav-item' : undefined}
                      sidebarIsCollapsed={sidebarIsCollapsed}
                    />
                  </ConditionalDisplay>

                  <ConditionalDisplay displayWhen={[canViewAttendance]}>
                    <DashboardNavItem
                      label="Attendance"
                      marginLeft="15px"
                      Icon={Team}
                      to={`/dashboard/attendance?`}
                      isActive={locationInfo.attendanceActive}
                      hover="exception"
                      id={locationInfo.attendanceActive ? 'active-dash-nav-item' : undefined}
                      sidebarIsCollapsed={sidebarIsCollapsed}
                    />
                  </ConditionalDisplay>

                  <ConditionalDisplay displayWhen={[canViewAdmins]}>
                    <DashboardNavItem
                      label="Admins"
                      marginLeft="15px"
                      Icon={AdminPanelSettings}
                      hover="exception"
                      to={'/dashboard/admins'}
                      isActive={locationInfo.adminsActive}
                      id={locationInfo.adminsActive ? 'active-dash-nav-item' : undefined}
                      sidebarIsCollapsed={sidebarIsCollapsed}
                    />
                  </ConditionalDisplay>
                </MobileNavItemGroupWrapper>
              </ConditionalDisplay>
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewBulkActions]}>
              <MobileNavItemGroupWrapperInactive>
                <MobileMenuLabel>Bulk Actions</MobileMenuLabel>

                <DashboardNavItem
                  hover="exception"
                  Icon={People}
                  isActive={locationInfo.bulkActionsActive}
                  id={locationInfo.bulkActionsActive ? 'active-dash-nav-item' : undefined}
                  label="Bulk Actions"
                  onClick={() => toggleMenuItem('BulkActions')}
                  sidebarIsCollapsed={sidebarIsCollapsed}
                  to={'/dashboard/bulk-action-selection'}
                />
              </MobileNavItemGroupWrapperInactive>
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewFinance]}>
              <MobileMenuLabel>Finance</MobileMenuLabel>

              <DashboardNavItem
                hasActiveChild={expandedMenuGroup === 'Finance' || isOnFinancePage}
                hiddenOnMobile={isMobile}
                hover="exception"
                Icon={FaReceipt}
                id={locationInfo.homeActive ? 'active-dash-nav-item' : undefined}
                label="Finance"
                onClick={() => toggleMenuItem('Finance')}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />

              <ConditionalDisplay displayWhen={[displayFinanceOpts]}>
                <MobileNavItemGroupWrapper>
                  <ConditionalDisplay displayWhen={[canViewPayslips]}>
                    <DashboardNavItem
                      label="Payslips"
                      marginLeft="15px"
                      Icon={FileInvoiceDollar}
                      to={`/dashboard/payslips`}
                      isActive={locationInfo.payslipsActive}
                      hover="exception"
                      id={locationInfo.payslipsActive ? 'active-dash-nav-item' : undefined}
                      sidebarIsCollapsed={sidebarIsCollapsed}
                    />
                  </ConditionalDisplay>

                  <ConditionalDisplay displayWhen={[canViewPayroll]}>
                    <DashboardNavItem
                      label="Payroll"
                      marginLeft="15px"
                      Icon={FileInvoiceDollar}
                      to={`/dashboard/payroll`}
                      isActive={locationInfo.payrollActive}
                      hover="exception"
                      id={locationInfo.payrollActive ? 'active-dash-nav-item' : undefined}
                      sidebarIsCollapsed={sidebarIsCollapsed}
                    />
                  </ConditionalDisplay>

                  <ConditionalDisplay displayWhen={[canViewBudgetCategories]}>
                    <DashboardNavItem
                      label="Budgets"
                      marginLeft="15px"
                      hover="exception"
                      Icon={Tags}
                      to={'/dashboard/budgets'}
                      isActive={locationInfo.budgetsActive}
                      id={locationInfo.budgetsActive ? 'active-dash-nav-item' : undefined}
                      sidebarIsCollapsed={sidebarIsCollapsed}
                    />
                  </ConditionalDisplay>

                  <ConditionalDisplay displayWhen={[canViewSuppliers]}>
                    <DashboardNavItem
                      label="Suppliers"
                      marginLeft="15px"
                      Icon={BusinessCenter}
                      to={'/dashboard/suppliers'}
                      isActive={locationInfo.suppliersActive}
                      hover="exception"
                      id={locationInfo.suppliersActive ? 'active-dash-nav-item' : undefined}
                      sidebarIsCollapsed={sidebarIsCollapsed}
                    />
                  </ConditionalDisplay>

                  <ConditionalDisplay displayWhen={[canViewPurchases]}>
                    <DashboardNavItem
                      label="Purchases"
                      marginLeft="15px"
                      Icon={Receipt}
                      to={'/dashboard/purchase-requests'}
                      isActive={locationInfo.purchaseRequestsActive}
                      hover="exception"
                      id={locationInfo.purchaseRequestsActive ? 'active-dash-nav-item' : undefined}
                      sidebarIsCollapsed={sidebarIsCollapsed}
                    />
                  </ConditionalDisplay>
                </MobileNavItemGroupWrapper>
              </ConditionalDisplay>
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewAdministrativeOpts]}>
              <MobileMenuLabel>Admin</MobileMenuLabel>

              <DashboardNavItem
                hasActiveChild={expandedMenuGroup === 'Admin' || isOnAdminPage}
                hiddenOnMobile={isMobile}
                hover="exception"
                Icon={FilePdf}
                id={locationInfo.homeActive ? 'active-dash-nav-item' : undefined}
                label="Admin"
                onClick={() => toggleMenuItem('Admin')}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />

              <ConditionalDisplay displayWhen={[displayAdminOpts]}>
                <MobileNavItemGroupWrapper>
                  <ConditionalDisplay displayWhen={[canViewDocuments]}>
                    <DashboardNavItem
                      label="Documents"
                      marginLeft="15px"
                      Icon={DocumentPdf}
                      to={'/dashboard/agreements?status=active'}
                      isActive={locationInfo.agreementsActive}
                      hover="exception"
                      id={locationInfo.agreementsActive ? 'active-dash-nav-item' : undefined}
                      sidebarIsCollapsed={sidebarIsCollapsed}
                    />
                  </ConditionalDisplay>

                  <ConditionalDisplay displayWhen={[canViewForms]}>
                    <DashboardNavItem
                      label="Forms"
                      marginLeft="15px"
                      Icon={ClipboardList}
                      to={'/dashboard/forms'}
                      isActive={locationInfo.formsActive}
                      hover="exception"
                      id={locationInfo.formsActive ? 'active-dash-nav-item' : undefined}
                      sidebarIsCollapsed={sidebarIsCollapsed}
                    />
                  </ConditionalDisplay>

                  <ConditionalDisplay displayWhen={[canViewEvents]}>
                    <DashboardNavItem
                      label="Events"
                      marginLeft="15px"
                      Icon={BpEventsIcon}
                      to={'/dashboard/platform-events'}
                      isActive={locationInfo.platformEventsPathActive}
                      id={
                        locationInfo.platformEventsPathActive ? 'active-dash-nav-item' : undefined
                      }
                    />
                  </ConditionalDisplay>

                  <ConditionalDisplay displayWhen={[canViewReports]}>
                    <DashboardNavItem
                      label="Reports"
                      marginLeft="15px"
                      Icon={TableCells}
                      hover="exception"
                      to={'/dashboard/activity-reports'}
                      isActive={locationInfo.reportsActive}
                      id={locationInfo.reportsActive ? 'active-dash-nav-item' : undefined}
                      sidebarIsCollapsed={sidebarIsCollapsed}
                    />
                  </ConditionalDisplay>

                  <ConditionalDisplay displayWhen={[canViewUserDebug]}>
                    <DashboardNavItem
                      label="Debug"
                      marginLeft="15px"
                      Icon={BugAnt}
                      hover="exception"
                      to={'/dashboard/debug-user'}
                      isActive={locationInfo.debugUserActive}
                      id={locationInfo.debugUserActive ? 'active-dash-nav-item' : undefined}
                      sidebarIsCollapsed={sidebarIsCollapsed}
                    />
                  </ConditionalDisplay>
                </MobileNavItemGroupWrapper>
              </ConditionalDisplay>
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewGrowth]}>
              <MobileMenuLabel>Growth</MobileMenuLabel>

              <DashboardNavItem
                hasActiveChild={expandedMenuGroup === 'ACE' || isOnAcePage}
                hiddenOnMobile={isMobile}
                hover="exception"
                Icon={Crown}
                id={locationInfo.homeActive ? 'active-dash-nav-item' : undefined}
                label="Growth"
                onClick={() => toggleMenuItem('ACE')}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />

              <ConditionalDisplay displayWhen={[displayAceOpts]}>
                <MobileNavItemGroupWrapper>
                  <ConditionalDisplay displayWhen={[canViewTrainingRecords]}>
                    <DashboardNavItem
                      label="Training"
                      marginLeft="15px"
                      Icon={School}
                      to={'/dashboard/training-records'}
                      isActive={locationInfo.skillsTrainingActive}
                      hover="exception"
                      id={locationInfo.skillsTrainingActive ? 'active-dash-nav-item' : undefined}
                      sidebarIsCollapsed={sidebarIsCollapsed}
                    />
                  </ConditionalDisplay>

                  <ConditionalDisplay displayWhen={[canViewWorkRecords]}>
                    <DashboardNavItem
                      label="Experience"
                      marginLeft="15px"
                      Icon={WorkHistory}
                      to={'/dashboard/work-records'}
                      isActive={locationInfo.workRecordsActive}
                      hover="exception"
                      id={locationInfo.workRecordsActive ? 'active-dash-nav-item' : undefined}
                      sidebarIsCollapsed={sidebarIsCollapsed}
                    />
                  </ConditionalDisplay>

                  <ConditionalDisplay displayWhen={[canViewSideHustles]}>
                    <DashboardNavItem
                      label="Side Hustles"
                      marginLeft="15px"
                      Icon={Storefront}
                      to={'/dashboard/side-hustles'}
                      isActive={locationInfo.sideHustlesActive}
                      hover="exception"
                      id={locationInfo.sideHustlesActive ? 'active-dash-nav-item' : undefined}
                      sidebarIsCollapsed={sidebarIsCollapsed}
                    />
                  </ConditionalDisplay>
                </MobileNavItemGroupWrapper>
              </ConditionalDisplay>
            </ConditionalDisplay>

            <MobileMenuLabel>Account</MobileMenuLabel>

            <DashboardNavItem
              hover="exception"
              Icon={Wrench}
              isActive={locationInfo.accountActive}
              id={locationInfo.accountActive ? 'active-dash-nav-item' : undefined}
              label="Account"
              onClick={() => toggleMenuItem('Account')}
              sidebarIsCollapsed={sidebarIsCollapsed}
              to={'/dashboard/account'}
            />
          </NavItems>
        </DashboardSideNav>

        <SidebarFooter>
          <DashCollapseIcon
            sidebarIsCollapsed={sidebarIsCollapsed}
            toggleSidebarCollapse={toggleSidebarCollapse}
          />

          {false && (
            <DashSettingsIcon
              sidebarIsCollapsed={sidebarIsCollapsed}
              toggleDashSidebarSettings={toggleDashSidebarSettings}
            />
          )}
        </SidebarFooter>
      </DashboardSideNavOuter>
    </Fragment>
  )
}

class SefSidebar extends Component {
  constructor(props) {
    super(props)

    this.state = {
      expandedMenuGroup: undefined
    }
  }

  toggleMenuItem(option) {
    if (this.state.expandedMenuGroup === option) {
      this.setState({ expandedMenuGroup: undefined })
    } else {
      this.setState({ expandedMenuGroup: option })
    }
  }

  render() {
    const { expandedMenuGroup } = this.state
    const {
      dashboardSettingsVisible,
      hasFetchedRoles,
      isParticipant,
      locationInfo,
      roles = [],
      sidebarIsCollapsed,
      toggleSidebarCollapse,
      toggleDashSidebarSettings,
      workspaceDetails = [],
      updateUser,
      user
    } = this.props

    const sefRoles = checkRoles({ roleData: roles })

    const hasPrivilegedRole = sefRoles.isPlatformAdmin || sefRoles.hasGodRole
    const hasPrivilegedOrSuperAdminRole = hasPrivilegedRole || sefRoles.isSuperAdmin
    const dashboardConfig = user.dashboardConfig || {}

    if (!hasFetchedRoles) {
      return (
        <Fragment>
          <DashboardHomePageMobileContainer displayMobile={locationInfo.homeActive}>
            <DashboardMobileHeading>Dashboard Menu</DashboardMobileHeading>
          </DashboardHomePageMobileContainer>

          <DashboardSideNavOuter displayMobile={locationInfo.homeActive}>
            <DashboardSideNav displayMobile={locationInfo.homeActive}>
              <NavItems id="dashboard-menu-items-wrapper">
                <DashboardNavItem
                  hiddenOnMobile={locationInfo.homeActive}
                  Icon={Dashboard}
                  hover="exception"
                  id={locationInfo.homeActive ? 'active-dash-nav-item' : undefined}
                  isActive={locationInfo.homeActive}
                  label="Dashboard"
                  to="/dashboard"
                />
              </NavItems>
            </DashboardSideNav>

            <SidebarFooterLogoWrapper>
              <FooterLink to="/">
                <BrowniePointsLogo src={dashSidebarLogo} />
              </FooterLink>
            </SidebarFooterLogoWrapper>
          </DashboardSideNavOuter>
        </Fragment>
      )
    }

    if (dashboardSettingsVisible) {
      return (
        <Fragment>
          <DashboardHeading>Select the dashboard navigation items you want to see</DashboardHeading>

          <DashboardSideNavOuter dashboardSettingsVisible displayMobile={locationInfo.homeActive}>
            <DashboardSettingsWrapper>
              <Formik
                initialValues={{
                  user_sidebar_config_update: true,
                  user_id: user.id,
                  ...dashboardConfig
                }}
                enableReinitialize
                validateOnChange={false}
                validateOnBlur={false}
                onSubmit={values => {
                  updateUser(values)
                  toggleDashSidebarSettings()
                }}
                render={({ handleSubmit }) => {
                  return (
                    <form onSubmit={handleSubmit}>
                      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        <DashSidebarConfigOption>
                          <Field disabled name="dash_sidenav_home" component={CheckboxInput} />
                          <Dashboard />
                          <span>Dashboard Home</span>
                        </DashSidebarConfigOption>

                        <ConditionalDisplay displayWhen={[hasPrivilegedRole]}>
                          <DashSidebarConfigOption>
                            <Field name="dash_sidenav_email_activity" component={CheckboxInput} />
                            <EmailActivityIcon />
                            <span>Email activity</span>
                          </DashSidebarConfigOption>
                        </ConditionalDisplay>

                        <ConditionalDisplay displayWhen={[false, hasPrivilegedRole]}>
                          <DashSidebarConfigOption>
                            <Field name="dash_sidenav_playbooks" component={CheckboxInput} />
                            <EmailActivityIcon />
                            <span>Playbooks</span>
                          </DashSidebarConfigOption>
                        </ConditionalDisplay>

                        <ConditionalDisplay displayWhen={[hasPrivilegedRole]}>
                          <DashSidebarConfigOption>
                            <Field name="dash_sidenav_bp_events" component={CheckboxInput} />
                            <BpEventsIcon />
                            <span>BP events</span>
                          </DashSidebarConfigOption>
                        </ConditionalDisplay>

                        <DashSidebarConfigOption>
                          <Field name="dash_sidenav_stories" component={CheckboxInput} />
                          <JournalText />
                          <span>Stories</span>
                        </DashSidebarConfigOption>

                        <DashSidebarConfigOption>
                          <Field name="dash_sidenav_contributions" component={CheckboxInput} />
                          <GiftFill />
                          <span>Contributions</span>
                        </DashSidebarConfigOption>

                        <DashSidebarConfigOption>
                          <Field name="dash_sidenav_likes" component={CheckboxInput} />
                          <SolidHeart />
                          <span>Likes</span>
                        </DashSidebarConfigOption>

                        <DashSidebarConfigOption>
                          <Field name="dash_sidenav_memberships" component={CheckboxInput} />
                          <DonationIcon />
                          <span>Memberships</span>
                        </DashSidebarConfigOption>

                        <DashSidebarConfigOption>
                          <Field name="dash_sidenav_opps" component={CheckboxInput} />
                          <OppIcon />
                          <span>{c(l('OPPS'))}</span>
                        </DashSidebarConfigOption>

                        <ConditionalDisplay displayWhen={[hasPrivilegedOrSuperAdminRole]}>
                          <DashSidebarConfigOption>
                            <Field name="dash_sidenav_camps" component={CheckboxInput} />
                            <Calendar2Range />
                            <span>{c(l('CAMPS'))}</span>
                          </DashSidebarConfigOption>
                        </ConditionalDisplay>

                        <DashSidebarConfigOption>
                          <Field name="dash_sidenav_orgs" component={CheckboxInput} />
                          <OrgIcon />
                          <span>{c(l('ORGS'))}</span>
                        </DashSidebarConfigOption>

                        <ConditionalDisplay displayWhen={[hasPrivilegedOrSuperAdminRole]}>
                          <DashSidebarConfigOption>
                            <Field name="dash_sidenav_people" component={CheckboxInput} />
                            <People />
                            <span>People</span>
                          </DashSidebarConfigOption>
                        </ConditionalDisplay>

                        <ConditionalDisplay displayWhen={[sefRoles.isSuperAdmin]}>
                          <DashSidebarConfigOption>
                            <Field disabled name="dash_sidenav_billing" component={CheckboxInput} />
                            <Receipt />
                            <span>Billing</span>
                          </DashSidebarConfigOption>
                        </ConditionalDisplay>

                        <DashSidebarConfigOption>
                          <Field disabled name="dash_sidenav_account" component={CheckboxInput} />
                          <Wrench />
                          <span>Account</span>
                        </DashSidebarConfigOption>
                      </div>

                      <div style={{ marginBottom: '10px', marginTop: '20px' }}>
                        <DashSidebarConfigOption>
                          <Field
                            name="dash_show_completed_and_dismissed_onboarding_tasks"
                            component={CheckboxInput}
                          />

                          <span>Show completed and dismissed onboarding challenges</span>
                        </DashSidebarConfigOption>
                      </div>

                      <div style={{ display: 'flex', marginTop: '10px' }}>
                        <div style={{ marginRight: '5px' }}>
                          <SmallButtonPrimary type="submit">Save</SmallButtonPrimary>
                        </div>

                        <LinkButton onClick={toggleDashSidebarSettings} type="button">
                          Cancel
                        </LinkButton>
                      </div>
                    </form>
                  )
                }}
              />
            </DashboardSettingsWrapper>
          </DashboardSideNavOuter>
        </Fragment>
      )
    }

    return (
      <PureComponentSidebar
        expandedMenuGroup={expandedMenuGroup}
        isParticipant={isParticipant}
        locationInfo={locationInfo}
        roles={roles}
        sefRoles={sefRoles}
        sidebarIsCollapsed={sidebarIsCollapsed}
        toggleDashSidebarSettings={toggleDashSidebarSettings}
        toggleMenuItem={val => this.toggleMenuItem(val)}
        toggleSidebarCollapse={toggleSidebarCollapse}
        workspaceDetails={workspaceDetails}
      />
    )
  }
}

export default SefSidebar
